import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, Inbox, MessageCircle, Bell, ClipboardList, ChevronRight, AlertCircle, CheckCircle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  request: { icon: ClipboardList, label: "Request", color: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
  thread: { icon: MessageCircle, label: "Message", color: "text-blue-600", bg: "bg-blue-50 border-blue-200" },
  notification: { icon: Bell, label: "Alert", color: "text-indigo-600", bg: "bg-indigo-50 border-indigo-200" },
};

const PRIORITY_COLORS = {
  critical: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-gray-100 text-gray-600 border-gray-200",
};

const STATUS_ICONS = {
  open: <AlertCircle className="h-3 w-3 text-amber-500" />,
  in_progress: <Clock className="h-3 w-3 text-blue-500" />,
  completed: <CheckCircle className="h-3 w-3 text-emerald-500" />,
};

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function UnifiedInbox({ onEntityNavigate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/comm/inbox`)
      .then(r => setItems(r.data.items || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? items : items.filter(i => i.inbox_type === filter);

  const FILTERS = [
    { id: "all", label: "All", count: items.length },
    { id: "request", label: "Requests", count: items.filter(i => i.inbox_type === "request").length },
    { id: "thread", label: "Messages", count: items.filter(i => i.inbox_type === "thread").length },
    { id: "notification", label: "Alerts", count: items.filter(i => i.inbox_type === "notification").length },
  ];

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;

  return (
    <div className="space-y-4" data-testid="unified-inbox">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-[#0A0A0A] flex items-center gap-2">
          <Inbox className="h-5 w-5 text-emerald-600" /> Unified Inbox
        </h3>
        <span className="text-xs text-[#888]">{items.length} items</span>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2" data-testid="inbox-filters">
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            data-testid={`inbox-filter-${f.id}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
              filter === f.id
                ? "bg-emerald-600 text-white border-emerald-600"
                : "bg-white text-[#555] border-[#e8e0d0] hover:border-emerald-300"
            }`}>
            {f.label} {f.count > 0 && <span className="ml-1 opacity-70">({f.count})</span>}
          </button>
        ))}
      </div>

      {/* Items List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 rounded-xl border border-[#e8e0d0] bg-white">
          <Inbox className="h-10 w-10 mx-auto text-[#ccc] mb-3" />
          <p className="text-sm text-[#888]">No items in your inbox</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((item) => {
            const conf = TYPE_CONFIG[item.inbox_type] || TYPE_CONFIG.notification;
            const Icon = conf.icon;
            return (
              <div key={`${item.inbox_type}-${item.id}`}
                className="rounded-xl border border-[#e8e0d0] bg-white hover:border-emerald-200 transition-colors cursor-pointer group"
                onClick={() => item.entity_id && onEntityNavigate?.(item.entity_type, item.entity_id)}
                data-testid={`inbox-item-${item.id}`}>
                <div className="flex items-start gap-3 p-4">
                  <div className={`mt-0.5 rounded-lg p-2 border ${conf.bg}`}>
                    <Icon className={`h-4 w-4 ${conf.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${conf.bg}`}>{conf.label}</span>
                      {item.priority && (
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${PRIORITY_COLORS[item.priority] || ""}`}>
                          {item.priority}
                        </span>
                      )}
                      {item.status && (
                        <span className="flex items-center gap-0.5 text-[9px] text-[#999]">
                          {STATUS_ICONS[item.status]} {item.status}
                        </span>
                      )}
                      {item.message_count > 0 && (
                        <span className="text-[9px] text-[#999]">{item.message_count} messages</span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-[#333] truncate">{item.title || "Untitled"}</p>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-[#999]">
                      {item.entity_type && <span className="capitalize">{item.entity_type}</span>}
                      <span>{timeAgo(item.created_at)}</span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[#ccc] group-hover:text-emerald-500 transition-colors mt-2 shrink-0" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
